import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * L16 — floating action bar shown at the bottom of a list page when
 * 1+ rows are selected. Hosts bulk-action buttons supplied by the
 * caller; clears via `onClear`. Hidden when `count === 0`.
 *
 * Sticks to the viewport bottom (rather than the page bottom) so the
 * bar stays visible during scroll on long lists.
 */
export function BulkActionBar({
  count,
  onClear,
  children,
  className,
}: {
  count: number;
  onClear: () => void;
  children: React.ReactNode;
  className?: string;
}) {
  const { t } = useTranslation();
  if (count === 0) return null;
  return (
    <div
      data-testid="bulk-action-bar"
      role="region"
      aria-label={t('bulk.actionBar.aria', { count })}
      className={cn(
        'pointer-events-none fixed inset-x-0 bottom-4 z-30 flex justify-center px-4',
        className,
      )}
    >
      <div className="pointer-events-auto flex items-center gap-3 rounded-full border bg-background/95 px-4 py-2 shadow-lg backdrop-blur">
        <span className="text-sm font-medium" data-testid="bulk-action-bar-count">
          {t('bulk.actionBar.selected', { count })}
        </span>
        <div className="flex items-center gap-1">{children}</div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClear}
          aria-label={t('bulk.actionBar.clear')}
          data-testid="bulk-action-bar-clear"
        >
          <X aria-hidden className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
